import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./ApiFormsLogsPage.css";

const METHOD_OPTIONS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

function getRole() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.role || "";
  } catch {
    return "";
  }
}

function isFormAndApiLogsEnabled() {
  return localStorage.getItem("formErrorLogsEnabled") === "true";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toUTCString();
}

function formatDuration(ms) {
  const value = Number(ms || 0);
  if (!Number.isFinite(value) || value < 0) return "-";
  return `${value} ms`;
}

function parseFilterDateYmd(value) {
  if (!value || typeof value !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function toFilterDateYmd(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

export default function ApiFormsLogsPage() {
  const role = getRole();
  const [logsMenuEnabled, setLogsMenuEnabled] = useState(() => isFormAndApiLogsEnabled());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logs, setLogs] = useState([]);
  const [query, setQuery] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [resultTypeFilter, setResultTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [clientSourceFilter, setClientSourceFilter] = useState("web");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeLog, setActiveLog] = useState(null);
  const [cleaningId, setCleaningId] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = async (nextPage = 1) => {
    setLoading(true);
    setError("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: String(nextPage),
        limit: String(limit),
      });
      if (query.trim()) params.set("search", query.trim());
      if (methodFilter) params.set("method", methodFilter);
      if (resultTypeFilter) params.set("resultType", resultTypeFilter);
      if (statusFilter) params.set("statusCode", statusFilter);
      if (dateFrom) params.set("fromDate", dateFrom);
      if (dateTo) params.set("toDate", dateTo);
      params.set("clientSource", clientSourceFilter || "web");
      const res = await fetch(`${baseUrl}/api/api-forms-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        setError(data?.message || "Failed to fetch API forms logs");
        setLogs([]);
        return;
      }
      const rows = Array.isArray(data.data) ? data.data : [];
      setLogs(rows);
      setPage(Number(data?.meta?.page || nextPage));
      setTotalPages(Math.max(1, Number(data?.meta?.totalPages || 1)));
    } catch {
      setError("Failed to fetch API forms logs");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchLogs(1);
    }, 250);
    return () => clearTimeout(delay);
  }, [query, methodFilter, resultTypeFilter, statusFilter, clientSourceFilter, dateFrom, dateTo]);

  useEffect(() => {
    const sync = () => setLogsMenuEnabled(isFormAndApiLogsEnabled());
    window.addEventListener("formErrorLogsVisibilityChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("formErrorLogsVisibilityChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const statusCodes = useMemo(() => {
    const values = new Set(
      logs
        .map((item) => Number(item.statusCode))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
    return [...values].sort((a, b) => a - b);
  }, [logs]);

  const handleClearFilters = () => {
    setQuery("");
    setMethodFilter("");
    setResultTypeFilter("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const handleClean = async (logId) => {
    if (!logId) return;
    if (!window.confirm("Are you sure you want to clean this log?")) return;
    try {
      setCleaningId(logId);
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/api/api-forms-logs/${logId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || "Failed to clean log");
        return;
      }
      if (activeLog?._id === logId) setActiveLog(null);
      fetchLogs(page);
    } finally {
      setCleaningId("");
    }
  };

  if (role !== "admin" || !logsMenuEnabled) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const sourceTab = clientSourceFilter === "app" ? "app" : "web";

  const handleSourceTab = (tab) => {
    setClientSourceFilter(tab === "app" ? "app" : "web");
    setPage(1);
  };

  return (
    <div className="api-forms-logs-page">
      <div className="api-forms-logs-card">
        <div className="api-forms-logs-head">
          <div className="api-forms-logs-title-row">
            <h3>Api Error Logs</h3>
            <div className="api-forms-source-tabs" role="tablist" aria-label="Filter by client">
              <button
                type="button"
                role="tab"
                aria-selected={sourceTab === "web"}
                className={sourceTab === "web" ? "active" : ""}
                onClick={() => handleSourceTab("web")}
              >
                Web
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={sourceTab === "app"}
                className={sourceTab === "app" ? "active" : ""}
                onClick={() => handleSourceTab("app")}
              >
                App
              </button>
            </div>
          </div>
          <div className="api-forms-logs-filters">
            <input
              type="text"
              placeholder="Search path, method, user, error..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
              <option value="">All Methods</option>
              {METHOD_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            <select value={resultTypeFilter} onChange={(e) => setResultTypeFilter(e.target.value)}>
              <option value="">All Results</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              {statusCodes.map((code) => (
                <option key={code} value={String(code)}>
                  {code}
                </option>
              ))}
            </select>
            <label className="api-forms-logs-date-label">
              <span>From</span>
              <DatePicker
                selected={parseFilterDateYmd(dateFrom)}
                onChange={(d) => {
                  const next = toFilterDateYmd(d);
                  setDateFrom(next);
                  if (dateTo && next && dateTo < next) setDateTo("");
                  setPage(1);
                }}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                isClearable
                className="api-forms-logs-date-input"
                wrapperClassName="api-forms-logs-datepicker-wrap"
                calendarClassName="api-forms-logs-theme-calendar"
                popperClassName="api-forms-logs-theme-calendar-popper"
              />
            </label>
            <label className="api-forms-logs-date-label">
              <span>To</span>
              <DatePicker
                selected={parseFilterDateYmd(dateTo)}
                onChange={(d) => {
                  setDateTo(toFilterDateYmd(d));
                  setPage(1);
                }}
                minDate={parseFilterDateYmd(dateFrom) || undefined}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                isClearable
                className="api-forms-logs-date-input"
                wrapperClassName="api-forms-logs-datepicker-wrap"
                calendarClassName="api-forms-logs-theme-calendar"
                popperClassName="api-forms-logs-theme-calendar-popper"
              />
            </label>
            <button type="button" onClick={handleClearFilters}>
              Reset
            </button>
          </div>
        </div>

        {loading ? <div className="api-forms-logs-state">Loading logs...</div> : null}
        {!loading && error ? <div className="api-forms-logs-state error">{error}</div> : null}

        {!loading && !error ? (
          <div className="api-forms-logs-table-wrap">
            <table className="api-forms-logs-table">
              <thead>
                <tr>
                  <th>Path</th>
                  <th>Method</th>
                  {sourceTab === "app" ? <th>Version</th> : null}
                  <th>User</th>
                  <th>Result</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!logs.length ? (
                  <tr>
                    <td colSpan={sourceTab === "app" ? 9 : 8} className="empty">
                      No logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((item) => (
                    <tr key={item._id}>
                      <td title={item.path || "-"}>{item.path || "-"}</td>
                      <td>{item.method || "-"}</td>
                      {sourceTab === "app" ? <td>{item.appVersion || "-"}</td> : null}
                      <td>{item.userName || item.userRole || "-"}</td>
                      <td>
                        <span className={`result-badge ${item.resultType === "error" ? "error" : "success"}`}>
                          {item.resultType || "-"}
                        </span>
                      </td>
                      <td>{item.statusCode || "-"}</td>
                      <td>{formatDuration(item.durationMs)}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td>
                        <div className="table-actions">
                          <button type="button" className="view-btn" onClick={() => setActiveLog(item)}>
                            View
                          </button>
                          <button
                            type="button"
                            className="clean-btn"
                            onClick={() => handleClean(item._id)}
                            disabled={cleaningId === item._id}
                          >
                            {cleaningId === item._id ? "Cleaning..." : "Clean"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="api-forms-logs-pagination">
              <button type="button" disabled={page <= 1 || loading} onClick={() => fetchLogs(page - 1)}>
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button type="button" disabled={page >= totalPages || loading} onClick={() => fetchLogs(page + 1)}>
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {activeLog ? (
        <div className="api-forms-modal-overlay" onClick={() => setActiveLog(null)}>
          <div className="api-forms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="api-forms-modal-head">
              <div>
                <h4>Api Error Log Details</h4>
              </div>
              <button type="button" onClick={() => setActiveLog(null)}>
                ×
              </button>
            </div>
            <div className="api-forms-modal-body">
              <div className="api-forms-modal-grid api-forms-modal-grid-extended">
                <div>
                  <span>Method</span>
                  <strong>{activeLog.method || "-"}</strong>
                </div>
                <div>
                  <span>Source</span>
                  <strong>
                    {activeLog.clientSource === "web" || activeLog.clientSource === "app" ? (
                      <span className={`source-badge ${activeLog.clientSource}`}>{activeLog.clientSource}</span>
                    ) : (
                      "-"
                    )}
                  </strong>
                </div>
                {sourceTab === "app" ? (
                  <div>
                    <span>Version</span>
                    <strong>{activeLog.appVersion || "-"}</strong>
                  </div>
                ) : null}
                <div>
                  <span>Result</span>
                  <strong>
                    {activeLog.resultType === "success" || activeLog.resultType === "error" ? (
                      <span className={`result-badge ${activeLog.resultType}`}>{activeLog.resultType}</span>
                    ) : (
                      activeLog.resultType || "-"
                    )}
                  </strong>
                </div>
                <div>
                  <span>Status Code</span>
                  <strong>{activeLog.statusCode || "-"}</strong>
                </div>
                <div>
                  <span>Duration</span>
                  <strong>{formatDuration(activeLog.durationMs)}</strong>
                </div>
                <div>
                  <span>User</span>
                  <strong>{activeLog.userName || activeLog.userRole || "-"}</strong>
                </div>
                <div>
                  <span>Created At</span>
                  <strong>{formatDateTime(activeLog.createdAt)}</strong>
                </div>
              </div>
              <div className="json-block">
                <h5>Request Body</h5>
                <pre>{JSON.stringify(activeLog.requestBody || {}, null, 2)}</pre>
              </div>
              <div className="json-block">
                <h5>Response Body</h5>
                <pre>{JSON.stringify(activeLog.responseBody || {}, null, 2)}</pre>
              </div>
              <div className="json-block">
                <h5>Error Message</h5>
                <pre>{JSON.stringify(activeLog.errorMessage || "", null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

